import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, FileEdit, RotateCcw, Upload, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const SECTIONS = [
  {
    key: 'hero_title',
    label: 'Hero Title',
    placeholder: 'RazientSounds',
    description: 'Main headline at the top of the landing page.',
    multiline: false,
  },
  {
    key: 'hero_tagline',
    label: 'Hero Tagline',
    placeholder: 'Discover artists. Explore their sound. Step into the virtual nightclub.',
    description: 'Short subtitle below the hero title. Use line breaks for multi-paragraph copy.',
    multiline: true,
  },
  {
    key: 'hero_background_url',
    label: 'Hero Background Image URL',
    placeholder: 'https://example.com/hero.jpg',
    description: 'Optional background image at the top of the landing page (paste a URL). Leave blank for the default cosmic background.',
    multiline: false,
    isUrl: true,
  },
  {
    key: 'nightclub_cta_title',
    label: 'Nightclub Section — Title',
    placeholder: 'Step Into The Virtual Nightclub',
    description: 'Headline of the nightclub call-to-action section.',
    multiline: false,
  },
  {
    key: 'nightclub_cta_text',
    label: 'Nightclub Section — Description',
    placeholder: 'Walk around, meet other people, and vibe together in a multiplayer space. No account needed to explore.',
    description: 'Body copy explaining the nightclub experience. Line breaks supported.',
    multiline: true,
  },
  {
    key: 'artist_cta_title',
    label: 'Artist CTA — Title',
    placeholder: 'Are you an artist?',
    description: 'Headline of the artist signup banner near the bottom.',
    multiline: false,
  },
  {
    key: 'artist_cta_text',
    label: 'Artist CTA — Description',
    placeholder: 'Create your profile, link your SoundCloud, and get discovered by our community.',
    description: 'Body copy inviting artists to sign up. Line breaks supported.',
    multiline: true,
  },
  {
    key: 'artist_cta_background_url',
    label: 'Artist CTA — Background Image URL',
    placeholder: 'https://example.com/banner.jpg',
    description: 'Optional banner background for the artist signup section. Leave blank for the default glass effect.',
    multiline: false,
    isUrl: true,
  },
];

export default function AdminSiteContent() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState({});
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);
  const fileInputs = useRef({});

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'owner', 'overseer'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchContent();
    // eslint-disable-next-line
  }, [user]);

  const fetchContent = async () => {
    try {
      const res = await apiClient.get('/site-content');
      setValues(res.data || {});
    } catch (e) {
      toast.error('Failed to load site content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await apiClient.put(`/site-content/${key}`, { value: values[key] || '' });
      toast.success('Saved — changes are live');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleReset = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await apiClient.put(`/site-content/${key}`, { value: '' });
      setValues(prev => ({ ...prev, [key]: '' }));
      toast.success('Reset to default');
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8 MB');
      return;
    }
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'site-content');
      const res = await apiClient.post('/upload-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      if (!url) throw new Error('No URL returned');
      // Persist immediately so admin sees the change live
      await apiClient.put(`/site-content/${key}`, { value: url });
      setValues(prev => ({ ...prev, [key]: url }));
      toast.success('Image uploaded — changes are live');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-site-content">
        <div className="flex items-center gap-3">
          <FileEdit className="w-7 h-7 text-violet-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Site Content</h1>
            <p className="text-sm text-white/60">Edit landing page copy live — changes apply instantly to all visitors.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-white/60 py-12 text-center">Loading…</div>
        ) : (
          SECTIONS.map(section => {
            const maxLen = section.isUrl ? 2000 : 1000;
            const currentVal = values[section.key] || '';
            return (
            <Card key={section.key} className="bg-slate-900/80 border-violet-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white text-lg">{section.label}</CardTitle>
                <p className="text-xs text-white/60 mt-1">{section.description}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.multiline ? (
                  <Textarea
                    data-testid={`site-content-input-${section.key}`}
                    value={currentVal}
                    onChange={(e) => handleChange(section.key, e.target.value)}
                    placeholder={section.placeholder}
                    maxLength={maxLen}
                    rows={4}
                    className="bg-black/60 border-white/20 text-white placeholder:text-white/30 whitespace-pre-line"
                  />
                ) : (
                  <Input
                    data-testid={`site-content-input-${section.key}`}
                    value={currentVal}
                    onChange={(e) => handleChange(section.key, e.target.value)}
                    placeholder={section.placeholder}
                    maxLength={maxLen}
                    className="bg-black/60 border-white/20 text-white placeholder:text-white/30"
                  />
                )}
                {section.isUrl && currentVal && (
                  <div
                    data-testid={`site-content-preview-${section.key}`}
                    className="rounded-md border border-white/10 bg-black/40 p-2"
                  >
                    <p className="text-xs text-white/40 mb-2">Preview:</p>
                    <img
                      src={currentVal}
                      alt={`${section.label} preview`}
                      className="max-h-40 w-auto rounded"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                {section.isUrl && (
                  <div className="flex items-center gap-2">
                    <input
                      ref={(el) => { fileInputs.current[section.key] = el; }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`site-content-file-${section.key}`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleUpload(section.key, file);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      data-testid={`site-content-upload-${section.key}`}
                      onClick={() => fileInputs.current[section.key]?.click()}
                      disabled={uploading[section.key]}
                      size="sm"
                      variant="outline"
                      className="border-violet-500/50 text-violet-300 hover:bg-violet-500/20"
                    >
                      {uploading[section.key] ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {uploading[section.key] ? 'Uploading…' : 'Upload image'}
                    </Button>
                    <span className="text-xs text-white/40">or paste a URL above</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`site-content-save-${section.key}`}
                    onClick={() => handleSave(section.key)}
                    disabled={saving[section.key]}
                    size="sm"
                    className="bg-violet-600 hover:bg-violet-500"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving[section.key] ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    data-testid={`site-content-reset-${section.key}`}
                    onClick={() => handleReset(section.key)}
                    disabled={saving[section.key]}
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-white/70 hover:bg-white/10"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset to default
                  </Button>
                  <span className="text-xs text-white/40 ml-auto">
                    {currentVal.length}/{maxLen}
                  </span>
                </div>
              </CardContent>
            </Card>
            );
          })
        )}
      </div>
    </AdminLayout>
  );
}
